// dashboard/frontend/src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);

// Debug function
const logAuthState = (action, data) => {
    console.log(`Auth State [${action}]:`, {
        timestamp: new Date().toISOString(),
        ...data,
        action
    });
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const initializeAuth = async () => {
            logAuthState('INIT', { loading: true });
            try {
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    const parsedUser = JSON.parse(storedUser);
                    logAuthState('STORED_USER_FOUND', { user: parsedUser });
                    
                    // Verify the stored user is still valid
                    try {
                        const response = await api.get('/auth/profile/');
                        if (response.data?.user) {
                            setUser(response.data.user);
                            logAuthState('USER_VERIFIED', { user: response.data.user });
                        } else {
                            throw new Error('Invalid user data');
                        }
                    } catch (verifyError) {
                        console.error('Stored user verification failed:', verifyError);
                        localStorage.removeItem('user');
                        setUser(null);
                        logAuthState('USER_VERIFICATION_FAILED', { error: verifyError });
                    }
                } else {
                    logAuthState('NO_STORED_USER');
                }
            } catch (err) {
                console.error('Auth initialization error:', err);
                setError(err);
                logAuthState('INIT_ERROR', { error: err });
            } finally {
                setLoading(false);
                logAuthState('INIT_COMPLETE', { loading: false });
            }
        };

        initializeAuth();
    }, []);

    const login = async (credentials) => {
        logAuthState('LOGIN_ATTEMPT', { credentials: { ...credentials, password: '***' } });
        try {
            const response = await api.post('/auth/login/', credentials);
            const userData = response.data.user;
            
            if (!userData) {
                throw new Error('No user data received from server');
            }

            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            logAuthState('LOGIN_SUCCESS', { user: userData });
            
            return userData;
        } catch (error) {
            console.error('Login error:', error);
            setError(error);
            logAuthState('LOGIN_ERROR', { error });
            throw error;
        }
    };

    const logout = async () => {
        logAuthState('LOGOUT_ATTEMPT', { currentUser: user });
        try {
            await api.post('/auth/logout/');
            logAuthState('LOGOUT_API_SUCCESS');
        } catch (error) {
            console.error('Logout API error:', error);
            logAuthState('LOGOUT_API_ERROR', { error });
        } finally {
            setUser(null);
            localStorage.removeItem('user');
            setError(null);
            logAuthState('LOGOUT_COMPLETE');
        }
    };

    // Debug effect for auth state changes
    useEffect(() => {
        logAuthState('STATE_CHANGE', { user, loading, error });
    }, [user, loading, error]);

    const getAuthState = () => ({
        user,
        loading,
        error,
        hasStoredUser: !!localStorage.getItem('user')
    });

    const clearAuth = () => {
        localStorage.removeItem('user');
        setUser(null);
        setError(null);
        logAuthState('DEBUG_CLEAR_AUTH');
    };

    const value = {
        user,
        login,
        logout,
        loading,
        error,
        // Debug methods
        getAuthState,
        clearAuth
    };

    if (loading) {
        console.log('AuthProvider is loading...');
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Enhanced useAuth hook with error handling
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        console.error('useAuth must be used within an AuthProvider');
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Export logAuthState for external use if needed
export { logAuthState };